import { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCallerUserProfile } from '../hooks/useQueries';
import {
  LayoutDashboard,
  Camera,
  FileText,
  BarChart3,
  Activity,
  LogOut,
  Cpu,
  Database,
  Wifi,
  User,
  ChevronRight,
} from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';

const ALL_NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['Operator', 'Quality Control Manager', 'Admin'] },
  { path: '/live-inspection', label: 'Live Inspection', icon: Camera, roles: ['Operator', 'Admin'] },
  { path: '/reports', label: 'Reports', icon: FileText, roles: ['Quality Control Manager', 'Admin'] },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['Quality Control Manager', 'Admin'] },
  { path: '/system-status', label: 'System Status', icon: Activity, roles: ['Admin'] },
];

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { logout, user } = useAuth();
  const queryClient = useQueryClient();
  const { data: userProfile } = useGetCallerUserProfile();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Filter nav items by user role
  const NAV_ITEMS = ALL_NAV_ITEMS.filter(item => {
    if (!user?.role) return item.path === '/dashboard';
    return item.roles.includes(user.role);
  });

  const handleLogout = async () => {
    await logout();
    queryClient.clear();
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className="w-60 flex-shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col">
        {/* Logo */}
        <div className="px-4 py-5 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-electric-blue/20 border border-electric-blue/30 flex items-center justify-center flex-shrink-0">
              <Cpu className="w-5 h-5 text-electric-blue" />
            </div>
            <div>
              <h1 className="text-sm font-black tracking-widest text-electric-blue uppercase leading-tight">
                VisionInspect
              </h1>
              <p className="text-[10px] text-muted-foreground tracking-widest uppercase font-mono leading-tight">
                AI Inspection
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-3">
            Navigation
          </p>
          {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
            const isActive = currentPath === path || currentPath.startsWith(path + '/');
            return (
              <Link
                key={path}
                to={path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-all group ${isActive
                  ? 'bg-electric-blue/15 text-electric-blue border border-electric-blue/30'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground border border-transparent'
                  }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-electric-blue' : 'text-muted-foreground group-hover:text-foreground'}`} />
                <span className="flex-1">{label}</span>
                {isActive && <ChevronRight className="w-3 h-3 text-electric-blue" />}
              </Link>
            );
          })}
        </nav>

        {/* User info & logout */}
        <div className="px-3 py-4 border-t border-sidebar-border space-y-2">
          <div className="flex items-center gap-2 px-2 py-2 rounded bg-muted/30">
            <div className="w-7 h-7 rounded bg-electric-blue/20 border border-electric-blue/30 flex items-center justify-center flex-shrink-0">
              <User className="w-3.5 h-3.5 text-electric-blue" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">
                {userProfile?.name || user?.displayName || 'Operator'}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">
                {userProfile?.role || user?.role || 'User'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-muted-foreground hover:text-status-danger hover:bg-status-danger/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="h-14 flex-shrink-0 bg-card border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">
              {NAV_ITEMS.find(n => currentPath.startsWith(n.path))?.label || 'Dashboard'}
            </span>
          </div>

          {/* System status indicators */}
          <div className="flex items-center gap-5">
            <StatusIndicator icon={Cpu} label="AI Model" status="Active" active />
            <StatusIndicator icon={Wifi} label="Camera" status="Connected" active />
            <StatusIndicator icon={Database} label="Database" status="Operational" active />
            <div className="h-6 w-px bg-border mx-2 hidden sm:block"></div>
            <ThemeToggle />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>

        {/* Footer */}
        <footer className="h-8 flex-shrink-0 bg-card border-t border-border flex items-center justify-between px-6">
          <span className="text-[10px] text-muted-foreground font-mono">
            © {new Date().getFullYear()} VisionInspect — Industrial AI Inspection Platform
          </span>
          <span className="text-[10px] text-muted-foreground">
            VisionInspect v1.0
          </span>
        </footer>
      </div>
    </div>
  );
}

function StatusIndicator({
  icon: Icon,
  label,
  status,
  active,
}: {
  icon: React.ElementType;
  label: string;
  status: string;
  active: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active ? 'bg-status-success glow-green' : 'bg-status-danger'}`} />
      <Icon className="w-3 h-3 text-muted-foreground" />
      <span className="text-muted-foreground hidden sm:inline">{label}:</span>
      <span className={active ? 'text-status-success' : 'text-status-danger'}>{status}</span>
    </div>
  );
}
